import { delay } from 'redux-saga';
import {
  all,
  call,
  put,
  takeEvery,
  takeLatest,
  take,
  select,
  fork,
} from 'redux-saga/effects';

import api from 'api/ApiClient';
// TODO: switch this to use named imports
// eslint-disable-next-line import/no-namespace
import * as actions from './actions';
import {
  pushFlashMessage,
  SPLIT_TREATMENTS_FETCH_REQUEST,
} from 'common/actions';
// TODO: switch this to use named imports
// eslint-disable-next-line import/no-namespace
import * as c from './appConstants';
import { fetchSplitTreatments } from 'common/sagas';
import convertOrderToSubscription from './sagas/convertOrderToSubscription';
import {
  getLineItemsProcessing,
  getPendingQuantity,
  getLineItems,
  getSidebarFocusState,
  getCartOpenState,
  getShouldAutoShowCommitmentUpsellModal,
  getCommitmentRewardLineItems,
} from './selectors';
import { FLASH_ERROR } from 'common/components/FlashMessages';

export const UPDATE_LINE_ITEM_QUANTITY_DELAY = 500;

export function* applyCouponToSession({ coupon }) {
  yield call(api.applyCouponToSession, coupon);
}

export function* applyCouponToOrder({ coupon }) {
  const { applied, order } = yield call(api.applyCouponToOrder, coupon);
  if (applied) {
    yield put(actions.saveCartSuccess(order));
  }
}

export function* updateCartLineItems(
  { lineItems, isCommitting = null, canShowSuccess = true },
  apiClient = api
) {
  try {
    const pendingQuantity = yield select(getPendingQuantity);
    const order = yield call(
      apiClient.updateCartLineItems,
      lineItems,
      isCommitting
    );
    yield put(actions.saveCartSuccess(order));
    if (canShowSuccess) {
      yield put(actions.showAddToCartMessage(pendingQuantity));
    }
  } catch (e) {
    yield put(actions.saveCartError(e.message));
  }
}

export function* updateLineItemQuantity({ lineItem, quantity }) {
  const offeringId = lineItem.variant_offering_id;
  yield put(
    actions.changeLineItemProcessingQuantity({ lineItem, quantity, offeringId })
  );
  yield call(delay, UPDATE_LINE_ITEM_QUANTITY_DELAY);
  const lineItems = yield select(getLineItemsProcessing);
  yield call(updateCartLineItems, { lineItems, canShowSuccess: false });
}

export function* requestOrderMembershipChange({ value }, apiClient = api) {
  try {
    const order = yield call(apiClient.changeOrderMembership, value);
    yield put(actions.saveCartSuccess(order, false));
  } catch (e) {
    yield put(actions.saveCartError(e.message));
  }
}

export function* removeLineItemsAndDisableOrderMembership() {
  const commitmentRewardLineItems = yield select(getCommitmentRewardLineItems);
  yield all(
    commitmentRewardLineItems.map(lineItem =>
      call(updateLineItemQuantity, { lineItem, quantity: 0 })
    )
  );
  yield put(actions.changeOrderMembership(false));
}

export function* autoClose() {
  yield call(delay, c.AUTO_CLOSE_CART_FOR_DESKTOP_DELAY);
  const isFocused = yield select(getSidebarFocusState);
  const isOpen = yield select(getCartOpenState);
  if (!isFocused && isOpen) {
    yield put(actions.toggleCartVisibility());
  }
}

export function* openCartForDesktop({ shouldAutoClose }) {
  if (window.outerWidth >= c.DESKTOP_WIDTH) {
    yield call(delay, c.OPEN_CART_FOR_DESKTOP_DELAY);
    yield put(actions.toggleCartVisibility(true));
    if (shouldAutoClose) {
      yield call(autoClose);
    }
  }
}

export function* wipeCart() {
  const lineItems = yield select(getLineItems);
  const lineItemsWiped = lineItems.map(lineItem => ({
    ...lineItem,
    quantity: 0,
  }));
  yield updateCartLineItems({ lineItems: lineItemsWiped });
}

export function* handleCartUpdateError({ error }) {
  yield put(pushFlashMessage(error, FLASH_ERROR));
  if (error.match(/csrf/i)) {
    yield call(delay, c.CSRF_RELOAD_TIMEOUT);
    window.location.reload();
  }
}

/**
 * This saga manages reopening the cart when the upsell modal
 * is opened and subsequently closed.
 *
 * We use delays before/after reopening so that the modals
 * that are being closed are given a tick to perform their clean up
 **/
export function* requestCommitmentUpsellModalVisibility(requestingToOpen) {
  if (requestingToOpen) {
    // collect the current state
    const state = yield select();

    // if the modal is open, then close it and wait for it to finish
    const cartWasOpen = state.isOpen;
    if (cartWasOpen) {
      //closing off the cart
      yield put(actions.toggleCartVisibility());
      //give the modal time to close
      yield call(delay, 0);
    }

    // opening the comitment upsell modal
    yield put(actions.changeCommitmentUpsellModalVisibility(true));

    if (cartWasOpen) {
      // wait until the modal is attempted to be closed
      yield take(actions.CHANGE_COMMITMENT_UPSELL_MODAL_VISIBILITY);

      //give the upsell modal time to close
      yield call(delay, 0);
      yield put(actions.toggleCartVisibility(true));

      // If an item is added to the cart make sure the cart reopens after it is added
      yield take(actions.SAVE_CART_SUCCESS);
      yield take(actions.TOGGLE_CART_VISIBILITY);
      yield put(actions.toggleCartVisibility(true));
    }
  } else {
    // directly grant the request to close without condition
    yield put(actions.changeCommitmentUpsellModalVisibility(false));
  }
}

/**
 * This saga implements the following flow:
 *
 * - If the redux state ever transitions to a new state based on a
 *   successful cart save, then we use our selector to determine
 *   if we need to automatically show the commitment upsell modal.
 */
export function* commitmentUpsellModalAutoVisibility() {
  // store the state prior to the next successful cart update
  const statePrev = yield select();

  // wait until the next sucessful cart update
  yield take(actions.SAVE_CART_SUCCESS);

  // determine whether we should automatically show the upsell modal
  const shouldShow = yield select(
    getShouldAutoShowCommitmentUpsellModal,
    statePrev
  );

  if (shouldShow) {
    yield put(actions.requestCommitmentUpsellModalVisibility(true));
  }
}

export const refreshYotpoWidget = variant => {
  const yotpoWidget =
    typeof document !== 'undefined' &&
    document.querySelector('[data-js="yotpo-reviews-widget"]');
  if (
    yotpoWidget &&
    yotpoWidget.getAttribute('data-product-id') !== variant.sku &&
    typeof yotpo !== 'undefined' // Global variable that gets set when Yotpo script is included
  ) {
    yotpoWidget.setAttribute('data-product-id', variant.sku);
    yotpo.refreshWidgets();
  }
};

export function* selectedVariantChange({ variant }) {
  yield call(refreshYotpoWidget, variant);
}

export function* fetchCartLineItemsCount() {
  try {
    const response = yield call(api.fetchCartLineItemsCount);
    yield put(actions.cartLineItemsCountLoaded(response));
  } catch {
    yield put({ type: actions.CART_LINE_ITEMS_COUNT_ERROR });
  }
}

export default function* saga() {
  // perform initial setup for cart line items
  yield fork(fetchCartLineItemsCount);
  yield takeLatest(actions.APPLY_COUPON_TO_SESSION, applyCouponToSession);

  yield takeLatest(SPLIT_TREATMENTS_FETCH_REQUEST, fetchSplitTreatments);
  yield takeLatest(actions.SAVE_CART_REQUEST, updateCartLineItems);
  yield takeLatest(actions.CHANGE_LINE_ITEM_QUANTITY, updateLineItemQuantity);
  yield takeLatest(actions.OPEN_CART_FOR_DESKTOP, openCartForDesktop);
  yield takeLatest(actions.WIPE_CART, wipeCart);
  yield takeEvery(
    actions.SAVE_CART_SUCCESS,
    commitmentUpsellModalAutoVisibility
  );
  yield takeEvery(actions.SAVE_CART_ERROR, handleCartUpdateError);
  yield takeLatest(
    actions.CHANGE_ORDER_MEMBERSHIP,
    requestOrderMembershipChange
  );
  yield takeEvery(
    actions.REQUEST_COMMITMENT_UPSELL_MODAL_VISIBILITY,
    requestCommitmentUpsellModalVisibility
  );
  yield takeEvery(
    actions.REMOVE_COMMITMENT_REWARD_LINE_ITEMS_AND_DISABLE_ORDER_MEMBERSHIP,
    removeLineItemsAndDisableOrderMembership
  );
  // setup the initial upsell modal flow watcher
  yield fork(commitmentUpsellModalAutoVisibility);
  yield takeLatest(
    actions.CHANGE_QUICK_SHOP_SELECTED_VARIANT,
    selectedVariantChange
  );
  yield takeEvery(
    actions.CONVERT_ORDER_TO_SUBSCRIPTION,
    convertOrderToSubscription
  );
  yield takeLatest(actions.APPLY_COUPON_TO_ORDER, applyCouponToOrder);
}
